import { useState } from 'react'

import { clsx } from 'clsx'

import {
  ModerationItem,
  Image,
  ModerationStatus,
  MassActionRequestContext,
  MemberListFilterByOperator,
  Member,
  MassActionRequest,
  PostStatus,
  ModerationOpMode,
} from '@tribeplatform/gql-client/types'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import {
  useCreateMassActionRequest,
  useUpdateModerationItem,
} from '@tribeplatform/react-sdk/hooks'
import { Alert } from '@tribeplatform/react-ui-kit/Alert'
import { Avatar } from '@tribeplatform/react-ui-kit/Avatar'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Checkbox } from '@tribeplatform/react-ui-kit/Checkbox'
import { Divider } from '@tribeplatform/react-ui-kit/Divider'
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { Modal } from '@tribeplatform/react-ui-kit/Modal'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { MassActionRequestAction } from '../../Admin/MassActions/types.js'

interface Props {
  open: boolean
  onClose: () => void
  moderationItem: ModerationItem
  setMassActionRequest: (value: MassActionRequest) => void
  action: ModerationOpMode
}

export const MemberRejectedModal = ({
  open,
  onClose,
  moderationItem,
  action,
  setMassActionRequest,
}: Props) => {
  const { $t } = useI18n()
  const { mutateAsync: updateModerationItem } = useUpdateModerationItem()
  const { mutateAsync: createMassActionRequest } = useCreateMassActionRequest()

  const [deleteOtherPosts, setDeleteOtherPosts] = useState<boolean>(true)
  const [loading, setLoading] = useState<boolean>(false)

  const profilePicture = (
    (moderationItem?.entity as Member)?.profilePicture as Image
  )?.url
  const name = (moderationItem?.entity as Member)?.name
  const tagline = (moderationItem?.entity as Member)?.tagline
  const memberId = (moderationItem?.entity as Member)?.id

  const rejectMember = async () => {
    setLoading(true)
    try {
      if (deleteOtherPosts) {
        const massActionRequest = await createMassActionRequest({
          input: {
            action: MassActionRequestAction.Delete,
            context: MassActionRequestContext.Post,
            extraProperties: {
              includeReplies: true,
            },
            filters: {
              filterBy: [
                {
                  key: 'ownerId',
                  operator: MemberListFilterByOperator.equals,
                  value: JSON.stringify(memberId),
                },
                {
                  key: 'status',
                  operator: MemberListFilterByOperator.in,
                  value: JSON.stringify([
                    PostStatus.PUBLISHED,
                    PostStatus.BLOCKED,
                  ]),
                },
              ],
            },
          },
        })
        setMassActionRequest(massActionRequest)
      }
      await updateModerationItem({
        id: moderationItem.id,
        input: {
          status: ModerationStatus.ACCEPTED,
          opMode: action,
        },
      })
      if (action === ModerationOpMode.Purge) {
        toast({
          title: $t({
            defaultMessage: '{MEMBER_CC} has been deleted',
            id: 'Moderation.Member.Deleted',
          }),
          status: 'success',
        })
      }
      if (action === ModerationOpMode.Retain) {
        toast({
          title: $t({
            defaultMessage: '{MEMBER_CC} has been suspended',
            id: 'Moderation.Member.Suspended',
          }),
          status: 'success',
        })
      }
      onClose()
    } catch (e) {
      toast({
        title: $t({
          defaultMessage: 'Something went wrong',
          id: 'Moderation.Action.Reject.Fail',
        }),
        status: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose}>
      <Modal.Content>
        <div className="flex flex-col">
          <div className="flex flex-col items-center">
            <Avatar size="4x" src={profilePicture} />
            <p className="text-content text-xl font-medium text-center mt-3">
              {name}
            </p>
            <p>{tagline}</p>
          </div>
          <div>
            <Divider />
          </div>
          <p className="text-content-subdued text-md">
            {action === ModerationOpMode.Purge && (
              <T
                defaultMessage="You are deleting {owner}'s account."
                id="Moderation.Member.Delete.Description"
                values={{ owner: name }}
              />
            )}
            {action === ModerationOpMode.Retain && (
              <T
                defaultMessage="You are suspending {owner}'s account."
                id="Moderation.Member.Suspend.Description"
                values={{ owner: name }}
              />
            )}
          </p>

          <FormControl.Label className="mt-6">
            <T
              defaultMessage="Would you like to delete all posts by this {MEMBER}?"
              id="Moderation.Member.DeleteAllPosts.Description"
            />
          </FormControl.Label>

          <div
            className={clsx(
              'flex cursor-pointer p-4 mt-3 rounded-md border',
              !deleteOtherPosts && 'border-line',
              deleteOtherPosts && 'border-focused bg-surface-pressed',
            )}
            onClick={() => setDeleteOtherPosts(v => !v)}
          >
            <div className="flex items-center">
              <Checkbox checked={deleteOtherPosts} />
            </div>
            <div className="flex-grow flex flex-col">
              <FormControl.Label className="cursor-pointer">
                <T
                  defaultMessage="Delete all other posts"
                  id="PostRejectedModal.DeleteAllOtherPosts"
                />
              </FormControl.Label>
            </div>
          </div>

          <Alert
            className="mt-6 py-2"
            status="error"
            icon={<SvgIcon name="status-warning" />}
          >
            <T
              defaultMessage="WARNING: Delete action is not reversible."
              id="Moderation.Member.DeleteAllPosts.Warning"
            />
          </Alert>
        </div>
      </Modal.Content>
      <Modal.Actions>
        <Button
          variant="primary"
          onClick={rejectMember}
          disabled={loading}
          loading={loading}
        >
          {action === ModerationOpMode.Purge && (
            <T id="Generics.Delete" defaultMessage="Delete" />
          )}
          {action === ModerationOpMode.Retain && (
            <T id="Generics.Suspend" defaultMessage="Suspend" />
          )}
        </Button>
        <Button variant="secondaryNeutral" onClick={onClose} disabled={loading}>
          <T defaultMessage="Cancel" id="Generics.Cancel" />
        </Button>
      </Modal.Actions>
    </Modal>
  )
}
